"use client";

import Link from "next/link";
import { useState } from "react";
import { motion } from "motion/react";
import Image from "next/image";
import {
  Check,
  X,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { PLANS } from "@/lib/subscription";

export default function PricingPage() {
  const [isAnnual, setIsAnnual] = useState(true);

  return (
    <main className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-neon-purple/10 via-dark-surface to-neon-pink/5" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-neon-purple/20 rounded-full blur-[150px] -z-10" />

      <nav className="relative z-10 flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
        <Link href="/" className="flex items-center gap-2">
          <Image src="/unicorn-icon.png" alt="Stashds" width={40} height={40} className="rounded-xl" />
          <span className="font-display text-xl font-bold">Stashds</span>
        </Link>
        <div className="flex items-center gap-4">
          <Link
            href="/login"
            className="text-white/70 hover:text-white transition-colors"
          >
            Log in
          </Link>
          <Link href="/register" className="btn-glass px-4 py-2">
            Get Started
          </Link>
        </div>
      </nav>

      <section className="relative z-10 px-6 pt-16 pb-24 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-display font-display font-bold mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Choose the plan that fits your needs. Upgrade or downgrade anytime.
          </p>

          <div className="flex items-center justify-center gap-4 mt-8">
            <span
              className={`text-sm ${!isAnnual ? "text-white" : "text-white/50"}`}
            >
              Monthly
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                isAnnual ? "bg-neon-purple" : "bg-white/20"
              }`}
            >
              <div
                className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                  isAnnual ? "left-8" : "left-1"
                }`}
              />
            </button>
            <span
              className={`text-sm ${isAnnual ? "text-white" : "text-white/50"}`}
            >
              Annual{" "}
              <span className="text-cyber-lime font-medium">Save 17%</span>
            </span>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`relative rounded-2xl p-8 ${
                plan.popular
                  ? "glass-card-elevated border-neon-purple/50 neon-glow-purple"
                  : "card-glass"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-neon-purple to-neon-pink text-xs font-semibold flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Most Popular
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-xl font-display font-semibold mb-1">
                  {plan.name}
                </h3>
                <p className="text-sm text-white/50">{plan.description}</p>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-display font-bold">
                    €{isAnnual ? Math.round(plan.priceAnnual / 12) : plan.price}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-white/50">/month</span>
                  )}
                </div>
                {plan.price > 0 && isAnnual && (
                  <p className="text-sm text-white/40 mt-1">
                    €{plan.priceAnnual} billed annually
                  </p>
                )}
              </div>

              <Link
                href={plan.price === 0 ? "/register" : "/register"}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
                  plan.popular
                    ? "btn-neon"
                    : "btn-glass hover:bg-white/20"
                }`}
              >
                {plan.cta}
                <ArrowRight className="w-4 h-4" />
              </Link>

              <div className="mt-8 space-y-3">
                {plan.features.map((feature) => (
                  <div
                    key={feature}
                    className="flex items-start gap-3 text-sm"
                  >
                    <Check className="w-4 h-4 text-cyber-lime flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{feature}</span>
                  </div>
                ))}
                {plan.limitations.map((limitation) => (
                  <div
                    key={limitation}
                    className="flex items-start gap-3 text-sm"
                  >
                    <X className="w-4 h-4 text-white/30 flex-shrink-0 mt-0.5" />
                    <span className="text-white/40">{limitation}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center"
        >
          <p className="text-white/50">
            Need a custom plan for your business?{" "}
            <Link
              href="/contact"
              className="text-neon-purple hover:text-neon-pink transition-colors"
            >
              Contact us
            </Link>
          </p>
        </motion.div>
      </section>
    </main>
  );
}
